import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { deleteDoc, getDoc, setDoc } from '@angular/fire/firestore'
import { deleteObject, getDownloadURL, uploadBytesResumable, UploadTask } from '@angular/fire/storage'
import { UntypedFormControl } from '@angular/forms'
import { MatSnackBar } from '@angular/material/snack-bar'
import moment from 'moment'
import { Observable, Subscription } from 'rxjs'
import { IdCardEmailService } from 'src/app/idcard/idcard-email.service'
import { IdCardRenewService } from 'src/app/idcard/idcard-renew.service'
import { DocumentStorageService } from 'src/app/service/document-storage.service'
import { DocumentEntry, DocumentType, DocumentTypeLabel } from 'src/domain/document'

export interface DeleteEvent {
  isNewDoc: boolean
  newDocId?: number
  doc?: DocumentEntryItem
}

export interface UploadEvent {
  newDocId: number
  file: File
  date: moment.Moment
  type: DocumentType
}

interface NewDocument {
  newDocId: number
  errorMessage?: string
  percentChange?: Observable<number>
  uploadTask: UploadTask
}

export interface DocumentEntryItem extends DocumentEntry {
  docId: string
}

export type DocumentEnumType = { key: string; value: any }

@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
    standalone: false
})
export class DocumentsComponent implements OnInit, OnDestroy {
  constructor(
    private auth: Auth,
    private snack: MatSnackBar,
    private docStorage: DocumentStorageService,
    private idCardRenewService: IdCardRenewService,
    private idCardEmailService: IdCardEmailService
  ) {}

  /**
   * Dog id or the trainer id
   */
  @Input()
  entityId: string

  /**
   * Firebase collection name
   */
  @Input()
  storageCollectionName: string

  @Input()
  documentTypes: DocumentEnumType[] = []

  @Input()
  get allDocuments(): DocumentEntryItem[] {
    return this._allDocuments
  }
  set allDocuments(docs: DocumentEntryItem[]) {
    this._allDocuments = docs
    this.filterDocuments(null)
  }
  private _allDocuments: DocumentEntryItem[] = []

  @Input()
  deleteDocumentOnUpload = false

  @Output()
  onRefresh = new EventEmitter<void>()

  private newDocumentId = 0

  documentTypeFilter = new UntypedFormControl(null)

  private sub = new Subscription()

  newDocuments: NewDocument[] = []

  filteredDocuments: DocumentEntryItem[] = []

  ngOnInit(): void {
    this.sub.add(this.documentTypeFilter.valueChanges.subscribe((key: string) => this.filterDocuments(key)))
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe()
  }

  trackByItem(index: number, item: NewDocument) {
    return item.newDocId
  }

  addNewDocument() {
    this.newDocumentId += 1
    this.newDocuments = [
      { newDocId: this.newDocumentId, errorMessage: undefined, percentChange: undefined, uploadTask: undefined },
      ...this.newDocuments,
    ]
  }

  private filterDocuments(typeKey: string) {
    if (typeKey) {
      this.filteredDocuments = this._allDocuments.filter((d) => d.type === typeKey)
    } else {
      this.filteredDocuments = this._allDocuments
    }
  }

  private getDocumentName(date: moment.Moment, type: DocumentType, filename: string) {
    return this.docStorage.getDocumentName(date, type, filename)
  }

  private getDocumentFirebaseDoc(docId: string) {
    return this.docStorage.getDocumentFirebaseDoc(this.storageCollectionName, this.entityId, docId)
  }

  private getDocumentStorageRef(docId: string) {
    return this.docStorage.getDocumentStorageRef(this.storageCollectionName, this.entityId, docId)
  }

  private percentageChanges(uploadTask: UploadTask): Observable<number> {
    return new Observable<number>((subscriber) => {
      return uploadTask.on(
        'state_changed',
        (snapshot) => subscriber.next((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        (error) => subscriber.error(error),
        () => subscriber.complete()
      )
    })
  }

  async upload(event: UploadEvent) {
    if (this.entityId) {
      const docId = this.getDocumentName(event.date, event.type, event.file.name)
      const docRef = this.getDocumentFirebaseDoc(docId)
      const existingDoc = await getDoc(docRef)
      if (existingDoc.exists()) {
        const humanName = this.documentTypes.filter((e) => e.key === event.type).map((e) => e.value)
        const errorMessage = `A ${event.date.format('YYYY.MM.DD')} dátummal már van dokumentum feltöltve a '${humanName}' típushoz.
        Törölje az előző dokumentumot ha újat szeretne feltölteni ezekkel a paraméterekkel.`
        this.syncNewDocuments(event.newDocId, { errorMessage })
        return
      }

      const storageRef = this.getDocumentStorageRef(docId)
      const uploadTask = uploadBytesResumable(storageRef, event.file)
      this.syncNewDocuments(event.newDocId, { percentChange: this.percentageChanges(uploadTask), uploadTask, errorMessage: undefined })

      try {
        await uploadTask
        const downloadUrl = await getDownloadURL(storageRef)
        setDoc(docRef, {
          documentDate: event.date.toDate(),
          downloadUrl,
          fileType: event.file.type,
          type: event.type,
          createdAt: new Date(),
          createdBy: this.auth.currentUser.email,
        })
        this.newDocuments = this.newDocuments.filter((d) => d.newDocId !== event.newDocId)
        this.onRefresh.emit()

        if (this.deleteDocumentOnUpload) {
          // always delete the missing documentation on new upload
          await this.docStorage.deleteExpiringDocumentEntry(this.entityId, event.type)
        }
      } catch (e) {
        if (e.code === 'storage/canceled') {
          console.log('User cancelled upload')
          this.filterOutNewDoc(event.newDocId)
          this.snack.open(`Feltöltés megszakítva ${event.date.format('YYYY.MM.DD')} - ${DocumentTypeLabel[event.type]}`, 'Ok', {
            duration: 5000,
          })
        } else {
          console.error('Upload failed', e)
          this.syncNewDocuments(event.newDocId, { errorMessage: 'Nem sikerült a feltöltés valami miatt.' })
        }
      }
    }
  }

  private filterOutNewDoc(newDocId: number) {
    this.newDocuments = this.newDocuments.filter((d) => d.newDocId !== newDocId)
  }

  private syncNewDocuments(newDocId: number, newDoc: Partial<NewDocument>) {
    // do the maneuver to make change detection work
    const updatedDocs = [...this.newDocuments]
    const spliceIdx = this.newDocuments.findIndex((d) => d.newDocId === newDocId)
    const prevDoc = this.newDocuments[spliceIdx]
    updatedDocs.splice(spliceIdx, 1, { ...prevDoc, ...newDoc })
    this.newDocuments = updatedDocs
  }

  async delete(event: DeleteEvent) {
    console.log('Delete event', event)
    if (event.isNewDoc) {
      this.newDocuments = this.newDocuments.filter((i) => {
        if (i.uploadTask?.cancel()) {
          console.log(i, 'task cancelled')
        }
        return i.newDocId !== event.newDocId
      })
    } else {
      const { doc: docItem } = event
      const storageRef = this.getDocumentStorageRef(docItem.docId)
      const storeRef = this.getDocumentFirebaseDoc(docItem.docId)

      try {
        await deleteObject(storageRef)
        await deleteDoc(storeRef)
        this.onRefresh.emit()
        this.snack.open('Dokumentum sikeresen törölve', 'Ok', { duration: 2000 })
      } catch (e) {
        console.error(e)
        this.snack.open('Valami baj történt a dokumentum törlése közben', 'Ajaj')
      }
    }
  }

  renew(document: DocumentEntryItem) {
    this.idCardRenewService.renew(this.entityId, document)
  }

  async sendEmail(document: DocumentEntryItem) {
    await this.idCardEmailService.sendPrintSheetEmail(this.entityId, document)
  }
}
